import type {
  QmsCapaActionType,
  QmsDepartment,
  QmsDepartmentSpikePattern,
  QmsTrendSlaStage,
} from '../../types/qms.types';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const STALE_AFTER_DAYS = 2;

const DEPARTMENT_LABELS: Record<QmsDepartment, string> = {
  product: 'Product',
  sales_marketing: 'Sales & Marketing',
  finance: 'Finance',
  it: 'IT',
  hr: 'HR',
  quality: 'Quality',
  operations: 'Operations',
};

export function formatQmsDepartmentName(department: string): string {
  return DEPARTMENT_LABELS[department as QmsDepartment] ?? department.replace(/_/g, ' ');
}

export function getDepartmentSpikeSummary(pattern: QmsDepartmentSpikePattern): {
  headline: string;
  detail: string;
} {
  if (pattern.previous_count === 0) {
    return {
      headline: 'New spike',
      detail: `${pattern.current_count} tickets in the last ${pattern.timespan_days} days after none in the previous ${pattern.timespan_days} days`,
    };
  }

  return {
    headline: `${pattern.increase_pct}% increase`,
    detail: `${pattern.current_count} tickets now vs ${pattern.previous_count} in the previous ${pattern.timespan_days} days`,
  };
}

export function formatQmsTrendSlaStage(stage: QmsTrendSlaStage): string {
  const labels: Record<QmsTrendSlaStage, string> = {
    intake_decision: 'Intake Decision',
    qd_approval: 'QD Approval',
    ceo_review: 'CEO Review',
    implementation: 'Implementation',
    validation: 'Validation',
  };

  return labels[stage] ?? stage.replace(/_/g, ' ');
}

export function formatQmsCapaAction(actionType: QmsCapaActionType): string {
  const labels: Record<QmsCapaActionType, string> = {
    immediate: 'Immediate Action',
    corrective: 'Corrective Action',
    preventive: 'Preventive Action',
  };

  return labels[actionType] ?? actionType.replace(/_/g, ' ');
}

export function getTrendFreshness(
  computedAt?: string | null,
  now: Date = new Date(),
): {
  isStale: boolean;
  ageDays: number | null;
  label: 'Fresh' | 'Stale' | 'Unknown';
  description: string;
} {
  if (!computedAt) {
    return {
      isStale: true,
      ageDays: null,
      label: 'Unknown',
      description: 'No computation timestamp is available.',
    };
  }

  const computedDate = new Date(computedAt);
  if (Number.isNaN(computedDate.getTime())) {
    return {
      isStale: true,
      ageDays: null,
      label: 'Unknown',
      description: 'The computation timestamp is invalid.',
    };
  }

  const ageDays = Math.max(0, Math.floor((now.getTime() - computedDate.getTime()) / MS_PER_DAY));
  const isStale = ageDays >= STALE_AFTER_DAYS;
  const ageText = ageDays === 0 ? 'today' : `${ageDays} day${ageDays === 1 ? '' : 's'} ago`;

  return {
    isStale,
    ageDays,
    label: isStale ? 'Stale' : 'Fresh',
    description: `Last computed ${ageText}. Scheduled daily at 06:00 UTC.`,
  };
}
